// =====================================================================
// Trajets.jsx - Pagina principala M-002 cu 3 tabs interne (Day 3)
// =====================================================================
// Layout Option C: 1 link in sidebar (/trajets), 3 vue-uri interne.
//
// Tabs:
//   1. Trajets (default)    - lista Trip + filtre + buton "+ Nouveau"
//   2. Facturation          - pivot Project x Date + sub-rows trip-uri + bulk
//   3. Transporteurs (cat)  - lista TripCarrier + buton "+ Nouveau"
//
// MesCourses ramane separat (driver mobile) — NU se inlocuieste.
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  trips as tripsApi,
  tripCarriers as carriersApi,
  projects as projectsApi,
  users as usersApi,
  vehicles as vehiclesApi,
} from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import TrajetFormModal from './TrajetFormModal.jsx';
import CarrierFormModal from './CarrierFormModal.jsx';
import MesCourses from './MesCourses.jsx';
import Breadcrumbs from '../../components/Breadcrumbs.jsx';
import { marqueurInactif } from '../../lib/tri-personnes.js';

const PAGE_SIZE = 80;

const STATUS_LABELS_FR = {
  PLANNED:   'Planifié',
  COMPLETED: 'Terminé',
  CANCELLED: 'Annulé',
};

const STATUS_BADGE_STYLE = {
  PLANNED:   { background: '#fef3c7', color: '#92400e' },
  COMPLETED: { background: '#d1fae5', color: '#065f46' },
  CANCELLED: { background: '#fee2e2', color: '#991b1b' },
};

const INVOICED_BADGE_STYLE = {
  yes: { background: '#d1fae5', color: '#065f46' },
  no:  { background: '#fef3c7', color: '#92400e' },
};

const inputBase = {
  padding: '0.4rem 0.5rem',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  fontSize: '0.8125rem',
  background: '#fff',
};

const btnPrimary = {
  background: '#2563eb', color: '#fff', border: 0,
  padding: '0.5rem 1rem', borderRadius: 4,
  fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

const btnSuccess = {
  background: '#059669', color: '#fff', border: 0,
  padding: '0.5rem 1rem', borderRadius: 4,
  fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

// Export buttons (M-002 Day 5) — CSV blue #3b82f6, XLSX green #16a34a
const exportBtnStyle = (format, loading) => ({
  background: format === 'csv' ? '#3b82f6' : '#16a34a',
  color: '#fff',
  border: 0,
  padding: '0.5rem 0.75rem',
  borderRadius: 4,
  fontWeight: 500,
  fontSize: '0.8125rem',
  cursor: loading ? 'wait' : 'pointer',
  opacity: loading ? 0.6 : 1,
});

const btnPager = (disabled) => ({
  padding: '0.25rem 0.5rem',
  background: 'transparent',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  fontSize: '0.875rem',
});

const thStyle = {
  padding: '0.625rem 0.75rem',
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  textAlign: 'left',
  borderBottom: '1px solid #e5e7eb',
};

const tdStyle = {
  padding: '0.625rem 0.75rem',
  verticalAlign: 'middle',
  color: '#374151',
  fontSize: '0.875rem',
};

const tabBtn = (active) => ({
  background: 'transparent',
  border: 0,
  padding: '0.75rem 1.25rem',
  fontSize: '0.875rem',
  fontWeight: active ? 600 : 400,
  color: active ? '#2563eb' : '#6b7280',
  borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
  cursor: 'pointer',
  marginBottom: -1,
});

function formatDateFR(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return ''; }
}

function fmtEur(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(n);
}

// =====================================================================
// MAIN
// =====================================================================
export default function Trajets() {
  const { can } = useAuth();

  const canReadTrips    = can('trips.read.all');
  const canCreateTrip   = can('trips.create.all');
  const canInvoiceTrip  = can('trips.update.all');
  const canReadCarriers = can('trips.read.all');     // reuz - tot encoder-ul vede
  const canCreateCarrier= can('trips.create.all');
  const canDoCourses    = can('trips.complete.assigned');  // Day 3 NEW
  const canManageVehicles = can('trips.manage_catalog.all'); // Day 11 — CRUD parc vehicules

  // Default tab depinde de permission:
  //   - Are read.all (encoder/admin) -> 'trajets'
  //   - Are doar complete.assigned (driver pur) -> 'mes-courses'
  const defaultTab = canReadTrips ? 'trajets' : (canDoCourses ? 'mes-courses' : 'trajets');

  // Day 9: support URL hash to force tab (e.g. /trajets#mes-courses from dashboard)
  const location = useLocation();
  const VALID_TABS = ['mes-courses', 'trajets', 'facturation', 'carriers', 'vehicles'];
  const hashTab = location.hash.replace('#', '');
  const initialTab = VALID_TABS.includes(hashTab) ? hashTab : defaultTab;
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (VALID_TABS.includes(hashTab)) setActiveTab(hashTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const TAB_LABELS = {
    'mes-courses': 'Mes courses',
    'trajets':     'Trajets',
    'facturation': 'Facturation',
    'carriers':    'Transporteurs (catalogue)',
    'vehicles':    'Véhicules (catalogue)',
  };

  // Access gate: needs cel putin una din permission-uri
  if (!canReadTrips && !canDoCourses) {
    return (
      <div style={{ padding: 40 }}>
        <h1>Trajets</h1>
        <p style={{ color: '#6b7280', marginTop: 16 }}>Permission refusée.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Trajets</h1>
        <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>M-002 — dispatch &amp; facturation</span>
      </div>
      <p className="hide-mobile" style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1.25rem' }}>
        Dispatch des trajets de transport et facturation aux clients.
      </p>

      <Breadcrumbs items={[
        { label: 'Trajets', onClick: () => setActiveTab(defaultTab) },
        { label: TAB_LABELS[activeTab] || activeTab },
      ]} />

      <div className="mobile-tabs-scroll" style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
        {canDoCourses && (
          <button style={tabBtn(activeTab === 'mes-courses')} onClick={() => setActiveTab('mes-courses')}>
            🚗 Mes courses
          </button>
        )}
        {canReadTrips && (
          <button style={tabBtn(activeTab === 'trajets')} onClick={() => setActiveTab('trajets')}>
            🚚 Trajets
          </button>
        )}
        {canReadTrips && (
          <button style={tabBtn(activeTab === 'facturation')} onClick={() => setActiveTab('facturation')}>
            💰 Facturation
          </button>
        )}
        {canReadCarriers && (
          <button style={tabBtn(activeTab === 'carriers')} onClick={() => setActiveTab('carriers')}>
            🎫 Transporteurs (catalogue)
          </button>
        )}
        {canReadTrips && (
          <button style={tabBtn(activeTab === 'vehicles')} onClick={() => setActiveTab('vehicles')}>
            🚐 Véhicules (catalogue)
          </button>
        )}
      </div>

      {activeTab === 'mes-courses' && canDoCourses    && <MesCourses embedded />}
      {activeTab === 'trajets'     && canReadTrips    && <TrajetsTab canCreate={canCreateTrip} />}
      {activeTab === 'facturation' && canReadTrips    && <FacturationTab canInvoice={canInvoiceTrip} />}
      {activeTab === 'carriers'    && canReadCarriers && <CarriersTab canCreate={canCreateCarrier} />}
      {activeTab === 'vehicles'    && canReadTrips    && <VehiclesTab canManage={canManageVehicles} />}
    </div>
  );
}

// =====================================================================
// TAB 1: TRAJETS (encoder)
// =====================================================================
function TrajetsTab({ canCreate }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [aggregates, setAggs]   = useState(null);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // Filtre
  const [search, setSearch]                   = useState('');
  const [filterStatus, setFilterStatus]       = useState('all');
  const [filterInvoiced, setFilterInvoiced]   = useState('');
  const [filterDriverId, setFilterDriverId]   = useState('all');
  const [filterProjectId, setFilterProjectId] = useState('all');
  const [filterDateFrom, setFilterDateFrom]   = useState('');
  const [filterDateTo, setFilterDateTo]       = useState('');

  // Dropdown data
  const [driversList, setDriversList]   = useState([]);
  const [projectsList, setProjectsList] = useState([]);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  function openCreate() { setEditingId(null); setShowModal(true); }
  function openEdit(id) { setEditingId(id); setShowModal(true); }
  function closeModal() { setShowModal(false); setEditingId(null); }
  function onSaved()    { closeModal(); fetchList(); }

  // Export (M-002 Day 5)
  const [exporting, setExporting] = useState(null); // null | 'csv' | 'xlsx'

  // Load dropdowns once
  useEffect(() => {
    (async () => {
      try {
        const [usersRes, projectsRes] = await Promise.all([
          usersApi.list({ driver: 'true' }),
          projectsApi.list(),
        ]);
        setDriversList(usersRes.items || usersRes || []);
        setProjectsList(Array.isArray(projectsRes) ? projectsRes : (projectsRes.items || []));
      } catch (err) {
        console.error('[TrajetsTab] dropdowns load error', err);
      }
    })();
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (search.trim())             params.q = search.trim();
      if (filterStatus !== 'all')    params.status = filterStatus;
      if (filterInvoiced)            params.invoiced = filterInvoiced;
      if (filterDriverId !== 'all')  params.driverId = filterDriverId;
      if (filterProjectId !== 'all') params.projectId = filterProjectId;
      if (filterDateFrom)            params.dateFrom = filterDateFrom;
      if (filterDateTo)              params.dateTo = filterDateTo;

      const res = await tripsApi.list(params);
      setItems(res.items || []);
      setTotal(res.total || 0);
      // Aggregates pe pagina curenta - backend trimite eventual totaluri
      setAggs({
        totalKm:       res.totalKm ?? null,
        totalMinutes:  res.totalMinutes ?? null,
        totalSale:     res.totalSale ?? null,
        totalPurchase: res.totalPurchase ?? null,
      });
    } catch (err) {
      console.error('[TrajetsTab] fetch:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterStatus, filterInvoiced, filterDriverId, filterProjectId, filterDateFrom, filterDateTo]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Export handler (M-002 Day 5) — folosește filtre curente
  const handleExport = useCallback(async (format) => {
    if (exporting) return;
    setExporting(format);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('format', format);
      if (search.trim())             qs.set('q', search.trim());
      if (filterStatus !== 'all')    qs.set('status', filterStatus);
      if (filterInvoiced)            qs.set('invoiced', filterInvoiced);
      if (filterDriverId !== 'all')  qs.set('driverId', filterDriverId);
      if (filterProjectId !== 'all') qs.set('projectId', filterProjectId);
      if (filterDateFrom)            qs.set('dateFrom', filterDateFrom);
      if (filterDateTo)              qs.set('dateTo', filterDateTo);

      const res = await fetch(`/api/trips/export?${qs.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          msg = body.message || body.error || msg;
        } catch (_) { /* not JSON */ }
        throw new Error(msg);
      }

      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      const dateStamp = new Date().toISOString().slice(0, 10);
      a.download = `trajets_${dateStamp}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[TrajetsTab] export error', err);
      setError(`Erreur export ${format.toUpperCase()}: ${err.message || err}`);
    } finally {
      setExporting(null);
    }
  }, [exporting, search, filterStatus, filterInvoiced, filterDriverId, filterProjectId, filterDateFrom, filterDateTo]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to   = Math.min(page * PAGE_SIZE, total);

  return (
    <>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Référence, origine, destination..."
          value={search}
          onChange={e => { setPage(1); setSearch(e.target.value); }}
          style={{ ...inputBase, flex: '1 1 220px' }}
        />
        <select value={filterStatus} onChange={e => { setPage(1); setFilterStatus(e.target.value); }} style={inputBase}>
          <option value="all">Tous statuts</option>
          <option value="PLANNED">Planifié</option>
          <option value="COMPLETED">Terminé</option>
          <option value="CANCELLED">Annulé</option>
        </select>
        <select value={filterInvoiced} onChange={e => { setPage(1); setFilterInvoiced(e.target.value); }} style={inputBase}>
          <option value="">Tous (facturé+non)</option>
          <option value="false">Non facturés</option>
          <option value="true">Facturés</option>
        </select>
        <select value={filterDriverId} onChange={e => { setPage(1); setFilterDriverId(e.target.value); }} style={inputBase}>
          <option value="all">Tous chauffeurs</option>
          {driversList.map(u => (
            <option key={u.id} value={u.id}>{u.name || u.username || u.id}{marqueurInactif(u)}</option>
          ))}
        </select>
        <select value={filterProjectId} onChange={e => { setPage(1); setFilterProjectId(e.target.value); }} style={inputBase}>
          <option value="all">Tous projets</option>
          {projectsList.map(p => (
            <option key={p.id} value={p.id}>{p.name || p.id}</option>
          ))}
        </select>
        <input type="date" value={filterDateFrom} onChange={e => { setPage(1); setFilterDateFrom(e.target.value); }} style={inputBase} title="Date début" />
        <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>→</span>
        <input type="date" value={filterDateTo} onChange={e => { setPage(1); setFilterDateTo(e.target.value); }} style={inputBase} title="Date fin" />

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting !== null || loading}
            style={exportBtnStyle('csv', exporting === 'csv')}
            title="Exporter en CSV"
          >
            {exporting === 'csv' ? '...' : `CSV (${total})`}
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null || loading}
            style={exportBtnStyle('xlsx', exporting === 'xlsx')}
            title="Exporter en XLSX"
          >
            {exporting === 'xlsx' ? '...' : 'XLSX'}
          </button>
          {canCreate && <button style={btnPrimary} onClick={openCreate}>+ Nouveau trajet</button>}
        </div>
      </div>

      {/* Pagination + count + aggregates */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', fontSize: '0.8125rem', color: '#6b7280', flexWrap: 'wrap', gap: '0.5rem' }}>
        <span>{from}-{to} / {total}</span>
        {aggregates && (aggregates.totalKm != null || aggregates.totalSale != null) && (
          <span style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {aggregates.totalKm != null && <span>Σ km: <strong style={{ color: '#374151' }}>{aggregates.totalKm.toLocaleString('fr-BE')}</strong></span>}
            {aggregates.totalMinutes != null && <span>Σ attente: <strong style={{ color: '#374151' }}>{aggregates.totalMinutes} min</strong></span>}
            {aggregates.totalSale != null && <span>Σ vente: <strong style={{ color: '#065f46' }}>{fmtEur(aggregates.totalSale)}</strong></span>}
            {aggregates.totalPurchase != null && <span>Σ achat: <strong style={{ color: '#991b1b' }}>{fmtEur(aggregates.totalPurchase)}</strong></span>}
          </span>
        )}
        <span>
          <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={btnPager(page <= 1)}>‹</button>
          <span style={{ margin: '0 0.5rem' }}>Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} style={btnPager(page >= totalPages)}>›</button>
        </span>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 4, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement...</div>}

      {!loading && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Réf</th>
                <th style={thStyle}>Projet</th>
                <th style={thStyle}>Itinéraire</th>
                <th style={thStyle}>Chauffeur</th>
                <th style={thStyle}>Transporteur</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Km</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Vente</th>
                <th style={thStyle}>Statut</th>
                <th style={thStyle}>Facturé</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={10} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>Aucun trajet pour ces filtres.</td></tr>
              )}
              {items.map(t => (
                <tr key={t.id} onClick={() => openEdit(t.id)} style={{ borderTop: '1px solid #f3f4f6', cursor: 'pointer' }}>
                  <td style={tdStyle}>{formatDateFR(t.date)}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280' }}>{t.ref}</td>
                  <td style={tdStyle}>{t.project?.name || '—'}</td>
                  <td style={tdStyle}>
                    {t.origin || '—'} <span style={{ color: '#9ca3af' }}>→</span> {t.destination || '—'}
                  </td>
                  <td style={tdStyle}>{t.driver?.name || t.driver?.username || '—'}</td>
                  <td style={{ ...tdStyle, fontSize: '0.8125rem' }}>{t.carrier?.label || t.carrier?.code || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{t.kmTotal != null ? `${t.kmTotal}` : '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#065f46' }}>{t.salePrice ? fmtEur(t.salePrice) : '—'}</td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.125rem 0.5rem', borderRadius: 12,
                      fontSize: '0.6875rem', fontWeight: 500,
                      ...(STATUS_BADGE_STYLE[t.status] || {}),
                    }}>
                      {STATUS_LABELS_FR[t.status] || t.status}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ ...(t.invoiced ? INVOICED_BADGE_STYLE.yes : INVOICED_BADGE_STYLE.no), padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500 }}>
                      {t.invoiced ? '✓' : '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <TrajetFormModal tripId={editingId} onClose={closeModal} onSaved={onSaved} />
      )}
    </>
  );
}

// =====================================================================
// TAB 2: FACTURATION (pivot Project x Date)
// =====================================================================
function FacturationTab({ canInvoice }) {
  const [trips, setTrips]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [selectedIds, setSelectedIds]   = useState(new Set());
  const [bulkBusy, setBulkBusy]         = useState(false);

  const [year, setYear]   = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelectedIds(new Set());
    try {
      const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const dateTo  = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      // Doar trips COMPLETED in luna (PLANNED nu se factureaza)
      const res = await tripsApi.list({ dateFrom, dateTo, status: 'COMPLETED', pageSize: 500 });
      setTrips(res.items || []);
    } catch (err) {
      console.error('[FacturationTab] fetch:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setTrips([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchTrips(); }, [fetchTrips]);

  // Build pivot: byProject = Map<projectId, {name, trips: [], allInvoiced, totalKm, totalSale, totalPurchase}>
  const pivot = (() => {
    const byProject = new Map();
    for (const t of trips) {
      const pkey = t.projectId || '(none)';
      const pname = t.project?.name || '(sans projet)';

      if (!byProject.has(pkey)) {
        byProject.set(pkey, {
          key: pkey, name: pname,
          trips: [], tripIds: [],
          allInvoiced: true,
          totalKm: 0, totalSale: 0, totalPurchase: 0,
        });
      }
      const proj = byProject.get(pkey);
      proj.trips.push(t);
      proj.tripIds.push(t.id);
      proj.totalKm       += t.kmTotal || 0;
      proj.totalSale     += t.salePrice || 0;
      proj.totalPurchase += t.purchasePrice || 0;
      if (!t.invoiced) proj.allInvoiced = false;
    }
    // Sort trips intern by date asc
    for (const proj of byProject.values()) {
      proj.trips.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    }
    return Array.from(byProject.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();

  function toggleProject(proj) {
    const next = new Set(selectedIds);
    const eligible = proj.trips.filter(t => !t.invoiced).map(t => t.id);
    const allSelected = eligible.every(id => next.has(id));
    if (allSelected) eligible.forEach(id => next.delete(id));
    else eligible.forEach(id => next.add(id));
    setSelectedIds(next);
  }

  function toggleTrip(tripId, invoiced) {
    if (invoiced) return; // can't select already invoiced
    const next = new Set(selectedIds);
    if (next.has(tripId)) next.delete(tripId);
    else next.add(tripId);
    setSelectedIds(next);
  }

  function toggleSelectAll() {
    const eligible = trips.filter(t => !t.invoiced).map(t => t.id);
    if (eligible.every(id => selectedIds.has(id))) setSelectedIds(new Set());
    else setSelectedIds(new Set(eligible));
  }

  async function handleBulkInvoice() {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Marquer ${selectedIds.size} trajet(s) comme facturé(s) ?`)) return;
    setBulkBusy(true);
    try {
      const res = await tripsApi.bulkInvoice({ ids: Array.from(selectedIds) });
      alert(`Facturés: ${res.invoiced}\nDéjà facturés (skip): ${res.alreadyInvoiced}\nIntrouvables: ${res.notFound}`);
      await fetchTrips();
    } catch (err) {
      console.error('[bulk invoice]', err);
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally { setBulkBusy(false); }
  }

  async function handleBulkUninvoice() {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Annuler la facturation pour ${selectedIds.size} trajet(s) ?`)) return;
    setBulkBusy(true);
    try {
      const res = await tripsApi.bulkUninvoice({ ids: Array.from(selectedIds) });
      alert(`Réversés: ${res.uninvoiced}\nDéjà non facturés (skip): ${res.alreadyUninvoiced}\nIntrouvables: ${res.notFound}`);
      await fetchTrips();
    } catch (err) {
      console.error('[bulk uninvoice]', err);
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally { setBulkBusy(false); }
  }

  const years = [year - 1, year, year + 1];
  const months = [
    { v: 1, l: 'Janvier' }, { v: 2, l: 'Février' }, { v: 3, l: 'Mars' }, { v: 4, l: 'Avril' },
    { v: 5, l: 'Mai' }, { v: 6, l: 'Juin' }, { v: 7, l: 'Juillet' }, { v: 8, l: 'Août' },
    { v: 9, l: 'Septembre' }, { v: 10, l: 'Octobre' }, { v: 11, l: 'Novembre' }, { v: 12, l: 'Décembre' },
  ];

  const grandTotal = pivot.reduce((acc, p) => ({
    km: acc.km + p.totalKm,
    sale: acc.sale + p.totalSale,
    purchase: acc.purchase + p.totalPurchase,
  }), { km: 0, sale: 0, purchase: 0 });

  return (
    <>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: 500 }}>Période:</span>
        <select value={month} onChange={e => setMonth(parseInt(e.target.value, 10))} style={inputBase}>
          {months.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
        </select>
        <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))} style={inputBase}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{selectedIds.size} sélectionné{selectedIds.size !== 1 ? 's' : ''}</span>
          {canInvoice && (
            <>
              <button style={{ ...btnSuccess, opacity: (selectedIds.size === 0 || bulkBusy) ? 0.5 : 1 }} disabled={selectedIds.size === 0 || bulkBusy} onClick={handleBulkInvoice}>
                ✓ Marquer facturé
              </button>
              <button style={{ ...btnPager(false), opacity: (selectedIds.size === 0 || bulkBusy) ? 0.5 : 1 }} disabled={selectedIds.size === 0 || bulkBusy} onClick={handleBulkUninvoice}>
                Annuler facturation
              </button>
            </>
          )}
        </div>
      </div>

      {/* Grand totals strip */}
      {!loading && trips.length > 0 && (
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', padding: '0.625rem 0.875rem', background: '#f9fafb', borderRadius: 4, marginBottom: '1rem', fontSize: '0.8125rem', color: '#374151' }}>
          <span>📊 <strong>{trips.length}</strong> trajets terminés</span>
          <span>Σ km: <strong>{grandTotal.km.toLocaleString('fr-BE')}</strong></span>
          <span>Σ vente: <strong style={{ color: '#065f46' }}>{fmtEur(grandTotal.sale)}</strong></span>
          <span>Σ achat: <strong style={{ color: '#991b1b' }}>{fmtEur(grandTotal.purchase)}</strong></span>
          <span>Marge: <strong style={{ color: '#0c447c' }}>{fmtEur(grandTotal.sale - grandTotal.purchase)}</strong></span>
        </div>
      )}

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 4, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement...</div>}

      {!loading && trips.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 6 }}>
          Aucun trajet terminé pour cette période.
        </div>
      )}

      {!loading && trips.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ ...thStyle, width: 36 }}>
                  <input
                    type="checkbox"
                    checked={trips.filter(t => !t.invoiced).every(t => selectedIds.has(t.id)) && trips.some(t => !t.invoiced)}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={thStyle}>Projet / Trajet</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Itinéraire</th>
                <th style={thStyle}>Chauffeur</th>
                <th style={thStyle}>Transporteur</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Km</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Vente</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {pivot.map(proj => (
                <ProjectRows key={proj.key} proj={proj} selectedIds={selectedIds} onToggleProject={() => toggleProject(proj)} onToggleTrip={toggleTrip} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ProjectRows({ proj, selectedIds, onToggleProject, onToggleTrip }) {
  const eligibleIds = proj.trips.filter(t => !t.invoiced).map(t => t.id);
  const totalSelected = eligibleIds.filter(id => selectedIds.has(id)).length;
  const allSelected = eligibleIds.length > 0 && totalSelected === eligibleIds.length;
  const someSelected = totalSelected > 0 && totalSelected < eligibleIds.length;

  return (
    <>
      {/* Project header row */}
      <tr style={{ borderTop: '2px solid #e5e7eb', background: '#fafafa' }}>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem' }}>
          <input
            type="checkbox"
            checked={allSelected}
            ref={el => { if (el) el.indeterminate = someSelected; }}
            onChange={onToggleProject}
            disabled={eligibleIds.length === 0}
          />
        </td>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem', fontWeight: 600, color: '#111827' }} colSpan={5}>
          {proj.name}
          <span style={{ marginLeft: 8, fontSize: '0.6875rem', color: '#6b7280', fontWeight: 400 }}>
            · {proj.trips.length} trajet{proj.trips.length > 1 ? 's' : ''}
          </span>
        </td>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 600 }}>{proj.totalKm.toLocaleString('fr-BE')}</td>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>{fmtEur(proj.totalSale)}</td>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem', textAlign: 'center' }}>
          <span style={{ ...(proj.allInvoiced ? INVOICED_BADGE_STYLE.yes : INVOICED_BADGE_STYLE.no), padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500 }}>
            {proj.allInvoiced ? '✓ Tous facturés' : 'Non facturé'}
          </span>
        </td>
      </tr>
      {/* Sub-rows: trip per trip */}
      {proj.trips.map(t => (
        <tr key={t.id} style={{ borderTop: '1px solid #f3f4f6' }}>
          <td style={tdStyle}>
            <input
              type="checkbox"
              checked={selectedIds.has(t.id)}
              onChange={() => onToggleTrip(t.id, t.invoiced)}
              disabled={t.invoiced}
            />
          </td>
          <td style={{ ...tdStyle, padding: '0.4rem 0.75rem 0.4rem 2rem', fontSize: '0.75rem', fontFamily: 'monospace', color: '#6b7280' }}>{t.ref}</td>
          <td style={tdStyle}>{formatDateFR(t.date)}</td>
          <td style={tdStyle}>{t.origin} <span style={{ color: '#9ca3af' }}>→</span> {t.destination}</td>
          <td style={tdStyle}>{t.driver?.name || '—'}</td>
          <td style={{ ...tdStyle, fontSize: '0.75rem' }}>{t.carrier?.label || t.carrier?.code || '—'}</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>{t.kmTotal || '—'}</td>
          <td style={{ ...tdStyle, textAlign: 'right', color: '#065f46' }}>{t.salePrice ? fmtEur(t.salePrice) : '—'}</td>
          <td style={{ ...tdStyle, textAlign: 'center' }}>
            <span style={{ ...(t.invoiced ? INVOICED_BADGE_STYLE.yes : INVOICED_BADGE_STYLE.no), padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500 }}>
              {t.invoiced ? '✓' : '—'}
            </span>
          </td>
        </tr>
      ))}
    </>
  );
}

// =====================================================================
// TAB 3: CARRIERS (catalogue)
// =====================================================================
function CarriersTab({ canCreate }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filterActive, setFilterActive] = useState('true');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  function openCreate() { setEditingId(null); setShowModal(true); }
  function openEdit(id) { setEditingId(id); setShowModal(true); }
  function closeModal() { setShowModal(false); setEditingId(null); }
  function onSaved()    { closeModal(); fetchList(); }

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { pageSize: 100 };
      if (filterActive) params.isActive = filterActive;
      const res = await carriersApi.list(params);
      // tripCarriers poate returna array direct sau {items}
      const arr = Array.isArray(res) ? res : (res.items || []);
      setItems(arr);
    } catch (err) {
      console.error('[CarriersTab] fetch:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filterActive]);

  useEffect(() => { fetchList(); }, [fetchList]);

  return (
    <>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <select value={filterActive} onChange={e => setFilterActive(e.target.value)} style={{ ...inputBase, minWidth: 120 }}>
          <option value="true">Actifs</option>
          <option value="false">Inactifs</option>
          <option value="">Tous</option>
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{items.length} transporteur{items.length !== 1 ? 's' : ''}</span>
          {canCreate && (
            <button style={btnPrimary} onClick={openCreate}>+ Nouveau transporteur</button>
          )}
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 4, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement...</div>}

      {!loading && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Libellé</th>
                <th style={thStyle}>Statut</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Trajets</th>
                <th style={thStyle}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>Aucun transporteur à afficher.</td></tr>
              )}
              {items.map(c => (
                <tr key={c.id} onClick={() => openEdit(c.id)} style={{ borderTop: '1px solid #f3f4f6', cursor: 'pointer' }}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600 }}>{c.code}</td>
                  <td style={tdStyle}>{c.label}</td>
                  <td style={tdStyle}>
                    <span style={{ ...(c.isActive ? INVOICED_BADGE_STYLE.yes : { background: '#f3f4f6', color: '#6b7280' }), padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500 }}>
                      {c.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#6b7280', fontSize: '0.8125rem' }}>{c._count?.trips ?? 0}</td>
                  <td style={{ ...tdStyle, fontSize: '0.75rem', color: '#9ca3af', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <CarrierFormModal carrierId={editingId} onClose={closeModal} onSaved={onSaved} />
      )}
    </>
  );
}

// =====================================================================
// TAB 5 : Véhicules (catalogue) — Day 11 (28-05-2026)
// =====================================================================
// CRUD inline du parc de véhicules. Lecture pour tous (trips.read.all),
// édition pour trips.manage_catalog.all.
function VehiclesTab({ canManage }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId] = useState(null);            // id en cours d'édition (ou 'new' pour création)
  const [formData, setFormData] = useState({ licensePlate: '', label: 'Camionnette', notes: '' });
  const [busy, setBusy] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = showInactive ? { showAll: '1' } : undefined;
      const data = await vehiclesApi.list(params);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur chargement véhicules');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { fetchList(); }, [fetchList]);

  function startNew() {
    setEditingId('new');
    setFormData({ licensePlate: '', label: 'Camionnette', notes: '' });
  }
  function startEdit(v) {
    setEditingId(v.id);
    setFormData({ licensePlate: v.licensePlate, label: v.label, notes: v.notes || '' });
  }
  function cancelEdit() {
    setEditingId(null);
    setFormData({ licensePlate: '', label: 'Camionnette', notes: '' });
  }

  async function saveRow() {
    if (!formData.licensePlate.trim() || !formData.label.trim()) {
      setError('Plaque et libellé obligatoires.');
      return;
    }
    setBusy(true); setError(null);
    try {
      if (editingId === 'new') {
        await vehiclesApi.create(formData);
      } else {
        await vehiclesApi.update(editingId, formData);
      }
      await fetchList();
      cancelEdit();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur sauvegarde');
    } finally { setBusy(false); }
  }

  async function toggleActive(v) {
    setBusy(true); setError(null);
    try {
      await vehiclesApi.update(v.id, { isActive: !v.isActive });
      await fetchList();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur toggle');
    } finally { setBusy(false); }
  }

  async function removeRow(v) {
    if (!window.confirm(`Supprimer la camionnette ${v.licensePlate} ?\nCette action est irréversible (si non utilisée par un trajet).`)) return;
    setBusy(true); setError(null);
    try {
      await vehiclesApi.remove(v.id);
      await fetchList();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur suppression');
    } finally { setBusy(false); }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Parc de véhicules</h2>
        <span style={{ fontSize: 13, color: '#9ca3af' }}>{items.length} véhicule{items.length !== 1 ? 's' : ''}</span>
        <div style={{ flex: 1 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569' }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Afficher inactifs
        </label>
        {canManage && editingId === null && (
          <button onClick={startNew} style={{ padding: '7px 14px', background: '#714B67', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            + Nouvelle camionnette
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: 10, background: '#fee2e2', color: '#991b1b', borderRadius: 6, marginBottom: 10, fontSize: 13 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#9ca3af' }}>Chargement…</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <thead>
            <tr style={{ background: '#f8fafc', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>
              <th style={vthStyle}>Plaque</th>
              <th style={vthStyle}>Libellé</th>
              <th style={vthStyle}>Notes</th>
              <th style={{ ...vthStyle, width: 90 }}>Statut</th>
              <th style={{ ...vthStyle, width: 140, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {editingId === 'new' && (
              <tr style={{ background: '#fbf9fd' }}>
                <td style={vtdStyle}>
                  <input value={formData.licensePlate} onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })}
                         placeholder="2-XXX-000" style={vinputStyle} autoFocus />
                </td>
                <td style={vtdStyle}>
                  <input value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                         placeholder="Camionnette" style={vinputStyle} />
                </td>
                <td style={vtdStyle}>
                  <input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                         placeholder="(optionnel)" style={vinputStyle} />
                </td>
                <td style={vtdStyle}>nouveau</td>
                <td style={{ ...vtdStyle, textAlign: 'right' }}>
                  <button onClick={saveRow} disabled={busy} style={vbtnSave}>✓ Enregistrer</button>
                  <button onClick={cancelEdit} disabled={busy} style={vbtnCancel}>✕</button>
                </td>
              </tr>
            )}
            {items.map((v) => editingId === v.id ? (
              <tr key={v.id} style={{ background: '#fbf9fd' }}>
                <td style={vtdStyle}><input value={formData.licensePlate} onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })} style={vinputStyle} /></td>
                <td style={vtdStyle}><input value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} style={vinputStyle} /></td>
                <td style={vtdStyle}><input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} style={vinputStyle} /></td>
                <td style={vtdStyle}>{v.isActive ? 'Actif' : 'Inactif'}</td>
                <td style={{ ...vtdStyle, textAlign: 'right' }}>
                  <button onClick={saveRow} disabled={busy} style={vbtnSave}>✓</button>
                  <button onClick={cancelEdit} disabled={busy} style={vbtnCancel}>✕</button>
                </td>
              </tr>
            ) : (
              <tr key={v.id} style={{ opacity: v.isActive ? 1 : 0.5 }}>
                <td style={{ ...vtdStyle, fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{v.licensePlate}</td>
                <td style={vtdStyle}>{v.label}</td>
                <td style={{ ...vtdStyle, color: '#9ca3af', fontSize: 12 }}>{v.notes || '—'}</td>
                <td style={vtdStyle}>
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 600,
                    background: v.isActive ? '#DCFCE7' : '#F3F4F6',
                    color:      v.isActive ? '#166534' : '#6B7280',
                  }}>{v.isActive ? 'Actif' : 'Inactif'}</span>
                </td>
                <td style={{ ...vtdStyle, textAlign: 'right' }}>
                  {canManage && (
                    <>
                      <button onClick={() => startEdit(v)} disabled={busy} title="Modifier" style={vbtnIcon}>✏️</button>
                      <button onClick={() => toggleActive(v)} disabled={busy} title={v.isActive ? 'Désactiver' : 'Activer'} style={vbtnIcon}>{v.isActive ? '🔒' : '🔓'}</button>
                      <button onClick={() => removeRow(v)} disabled={busy} title="Supprimer" style={{ ...vbtnIcon, color: '#dc2626' }}>🗑</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && editingId !== 'new' && (
              <tr><td colSpan={5} style={{ padding: 30, textAlign: 'center', color: '#9ca3af' }}>Aucun véhicule.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

const vthStyle = { padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 };
const vtdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13 };
const vinputStyle = { width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 };
const vbtnSave = { padding: '5px 10px', background: '#16A085', color: '#fff', border: 0, borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600, marginRight: 4 };
const vbtnCancel = { padding: '5px 10px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 12 };
const vbtnIcon = { padding: '4px 7px', background: 'transparent', border: 0, cursor: 'pointer', fontSize: 14, marginLeft: 4 };
